import PubSub from 'pubsub-js';
import gsap from 'gsap';
import { TOPIC_IDS } from '@/js/constants';

/**
 * @name DetailsAccordion
 * @description アコーディオンの開閉を制御するクラス
 * [data-toggler] 属性を持つ要素（通常はsummery）をクリックすると、[data-content] 属性を持つ要素が開閉する
 *
 * @param {string} selector - アコーディオンの親要素のセレクタ
 * @param {object} options - オプション
 * @param {string} options.easing - アニメーションのイージング
 * @param {number} options.duration - アニメーションの時間
 * @param {string} options.direction - アニメーションの方向
 * @param {string} options.breakpoints - ブレイクポイント
 * @example new DetailsAccordion('.js-accordion', { easing: 'ease-in-out', duration: 500, breakpoints: { '(max-width: 768px)': true } });
 *
 * TODO:
 * - reverse処理、Web Animations APIで可能ならgsapから変える
 */

export default class DetailsAccordion {
  constructor(selector, options) {
    this.el = [...document.querySelectorAll(selector)];
    this.options = {
      easing: 'ease-out',
      duration: 300,
      direction: '',
      breakpoints: null, // { '(max-width: px)', boolean }
      ...options,
    };
    if (this.el.length) {
      this._initialize();
    }
  }

  _initialize() {
    this.open = this.open.bind(this);
    this.shrink = this.shrink.bind(this);
    this.onResize = this.onResize.bind(this);

    PubSub.subscribe(TOPIC_IDS.WINDOW_CHANGE_BREAKPOINT, this.onResize);

    this.instances = this.el.map((it) => {
      const summary = it.querySelector('summary');
      const togglers = it.querySelectorAll('[data-toggler]');
      const content = it.querySelector('[data-content]');

      const state = {
        el: it,
        summary,
        content,
        animation: null,
        isClosing: false,
        isExpanding: false,
        options: { ...this.options },
        isActive: false,
      };
      [...togglers, summary].forEach((toggler) => {
        if (state.content) {
          toggler.$accordion = state;
          toggler.addEventListener('click', this.handleClickToggler.bind(this));
        } else {
          state.el.classList.add('is-disabled');
        }
      });
      return state;
    });

    this.onResize();

    this.instances.forEach((accordion) => {
      accordion.el.classList.add('is-initialized');

      // カレントページに関連するカテゴリを開く
      const hasCurrentPage = accordion.el.querySelector('.o-directory__page.-current, .o-directory__subpage.-current');

      if (hasCurrentPage || accordion.el.hasAttribute('open')) {
        // カレントページがある、または明示的にopen属性がある場合は開く
        accordion.el.setAttribute('open', '');
        accordion.el.classList.add('is-open');

        // コンテンツの表示を確実にする
        const contentWrapper = accordion.el.querySelector('.o-directory__pages__wrapper');
        if (contentWrapper) {
          contentWrapper.style.opacity = '1';
        }
      }
    });
  }

  onResize() {
    this.instances.forEach((accordion) => {
      if (!accordion.options.breakpoints) {
        return;
      }
      Object.keys(accordion.options.breakpoints).forEach((query) => {
        const matched = window.matchMedia(query).matches;
        if (matched) {
          accordion.isActive = accordion.options.breakpoints[query];
          if (!accordion.isActive) {
            accordion.el.open = false;
          }
          accordion.el.classList[accordion.isActive ? 'remove' : 'add']('is-disabled');
          accordion.summary[accordion.isActive ? 'removeEventListener' : 'addEventListener']('click', this.canceler);
        }
      });
    });
  }

  canceler(e) {
    if (!e.currentTarget.querySelector('a')) {
      e.preventDefault();
    }
  }

  parseBreakpoints(str) {
    if (!str) {
      return [];
    }
    return str.split(',').map((point) => {
      const parsed = point.split('@');
      return { [parsed[0]]: parsed[1] === 'true' };
    });
  }

  handleClickToggler(event) {
    const state = event.currentTarget.$accordion;

    // sp menu用
    const target = event.target.closest('.o-global-nav__link');
    // childrenを持たないメニューはdata-sp-expandedがついていない
    // targetをアコーディオンさせず、そのままクリックでページ遷移
    if (target && target.dataset.spExpanded === undefined) {
      return;
    }

    if (!state.isActive) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const root = state.el;
    // const navContent = root.querySelector('.o-global-nav__detail');

    // navContent.style.overflow = 'hidden';
    // if (state.options.direction !== 'reverse') root.style.overflow = 'hidden';

    if (root.open) {
      // クリックした瞬間にis-openクラスを削除
      state.el.classList.remove('is-open');
      this.shrink(state);
    } else {
      this.open(state);
    }
  }

  open(state) {
    if (state.options.direction !== 'reverse') state.el.style.height = `${state.el.offsetHeight}px`;
    state.el.open = true;
    state.el.classList.add('is-open');
    window.requestAnimationFrame(() => this.expand(state));
  }

  getPadding(el) {
    const paddingTop = parseFloat(getComputedStyle(el).paddingTop);
    const paddingBottom = parseFloat(getComputedStyle(el).paddingBottom);
    return {
      top: paddingTop || 0,
      bottom: paddingBottom || 0,
    };
  }

  async expand(state) {
    let keyframes = null;
    state.isExpanding = true;

    if (state.animation) {
      state.animation.cancel();
    }

    if (state.options.direction === 'reverse') {
      await gsap.fromTo(
        state.content,
        {
          duration: 0,
          clipPath: 'inset(100% 0 0 0)',
          ease: state.options.easing || 'power2.inOut',
        },
        {
          duration: state.options.duration / 1000,
          clipPath: 'inset(0% 0 0 0)',
          ease: state.options.easing || 'power2.inOut',
        },
      );
      this.onAnimationFinish(state, true);
      return;
    } else {
      const padding = this.getPadding(state.el);
      const startHeight = `${state.el.offsetHeight}px`;
      const endHeight = `${state.summary.offsetHeight + state.content.offsetHeight + padding.top + padding.bottom}px`;

      keyframes = {
        height: [startHeight, endHeight],
      };
    }

    state.animation = state.el.animate(keyframes, {
      duration: state.options.duration,
      easing: state.options.easing,
    });
    state.animation.onfinish = () => this.onAnimationFinish(state, true);
    state.animation.oncancel = () => (state.isExpanding = false);

    const navContent = state.el.querySelector('.o-global-nav__detail');
    if (navContent && window.matchMedia('(max-width: 768px)').matches) {
      // 初期状態は navContent の opacity は 0
      navContent.style.opacity = '0';
      gsap.to(navContent, {
        opacity: 1,
        duration: state.options.duration / 1000,
        ease: state.options.easing || 'power2.inOut',
      });
    }
    const contentWrapper = state.el.querySelector('.o-directory__pages__wrapper');
    if (contentWrapper) {
      contentWrapper.style.opacity = '0';

      setTimeout(() => {
        contentWrapper.style.opacity = '1';
      }, 20);
    }
  }

  async shrink(state) {
    let keyframes = null;
    state.isClosing = true;

    if (state.animation) {
      state.animation.cancel();
    }

    if (state.options.direction === 'reverse') {
      await gsap.fromTo(
        state.content,
        {
          duration: 0,
          clipPath: 'inset(0% 0 0 0)',
          ease: state.options.easing || 'power2.inOut',
        },
        {
          duration: state.options.duration / 1000,
          clipPath: 'inset(100% 0 0 0)',
          ease: state.options.easing || 'power2.inOut',
        },
      );
      this.onAnimationFinish(state, false);
      return;
    } else {
      const padding = this.getPadding(state.el);
      const startHeight = `${state.el.offsetHeight}px`;
      const endHeight = `${state.summary.offsetHeight + padding.top + padding.bottom}px`;
      keyframes = {
        height: [startHeight, endHeight],
      };
    }

    state.animation = state.el.animate(keyframes, {
      duration: state.options.duration,
      easing: state.options.easing,
    });

    state.animation.onfinish = () => {
      this.onAnimationFinish(state, false);
      // アニメーション完了後にクラスを削除
      state.el.classList.remove('is-open');
    };
    state.animation.oncancel = () => (state.isClosing = false);

    const navContent = state.el.querySelector('.o-global-nav__detail');
    if (navContent) {
      gsap.to(navContent, {
        opacity: 0,
        duration: (state.options.duration / 1000) * 0.5,
        ease: state.options.easing || 'power2.inOut',
      });
    }

    const contentWrapper = state.el.querySelector('.o-directory__pages__wrapper');
    if (contentWrapper) {
      // 閉じる際にはopacityを0に設定
      contentWrapper.style.opacity = '0';
    }
  }

  onAnimationFinish(state, open) {
    state.el.open = open;
    state.animation = null;
    state.isClosing = false;
    state.isExpanding = false;
    state.el.style.height = state.el.style.overflow = '';
  }
}
